import React, { useEffect, useState, useRef } from 'react';
import { Box, Paper, TextField, IconButton, Typography, Button } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs from 'dayjs';

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ExpediaDateRangeInput = ({
    label = 'Stay dates',
    startDate = '',
    endDate = '',
    onChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);
    const [selectingStart, setSelectingStart] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(dayjs());
    const containerRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            setTempStartDate(startDate);
            setTempEndDate(endDate);
        }
    }, [isOpen, startDate, endDate]);

    useEffect(() => {
        if (isOpen) {
            setTempStartDate(startDate);
            setTempEndDate(endDate);
            setSelectingStart(true);
            setCurrentMonth(startDate ? dayjs(startDate) : dayjs());
        }
    }, [isOpen, startDate, endDate]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setTempStartDate(startDate);
                setTempEndDate(endDate);
                setSelectingStart(true);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, startDate, endDate]);

    const formatDisplayDate = (value) => {
        if (!value) return '';
        return dayjs(value).format('DD MMM');
    };

    const getDisplayText = () => {
        if (startDate && endDate) {
            const start = formatDisplayDate(startDate);
            const end = formatDisplayDate(endDate);
            const duration = dayjs(endDate).diff(dayjs(startDate), 'day');
            return `${start} - ${end} (${duration} day${duration === 1 ? '' : 's'})`;
        }
        if (startDate) {
            return `${formatDisplayDate(startDate)} - Select return`;
        }
        return 'Select departure and return dates';
    };

    const handleDateClick = (date) => {
        const dateStr = date.format('YYYY-MM-DD');
        if (selectingStart) {
            setTempStartDate(dateStr);
            setTempEndDate('');
            setSelectingStart(false);
            return;
        }

        if (!tempStartDate) {
            setTempStartDate(dateStr);
            setSelectingStart(false);
            return;
        }

        const dayjsStart = dayjs(tempStartDate);
        if (date.isBefore(dayjsStart, 'day') || date.isSame(dayjsStart, 'day')) {
            setTempStartDate(dateStr);
            setTempEndDate('');
            setSelectingStart(false);
            return;
        }

        setTempEndDate(dateStr);
        if (onChange) {
            onChange(tempStartDate, dateStr);
        }
        setIsOpen(false);
        setSelectingStart(true);
    };

    const handleStartNewSelection = () => {
        setTempStartDate('');
        setTempEndDate('');
        setSelectingStart(true);
    };

    const daysInMonth = (monthDate) => {
        const days = [];
        const startOfMonth = dayjs(monthDate).startOf('month');
        const endOfMonth = dayjs(monthDate).endOf('month');
        const totalDays = endOfMonth.diff(startOfMonth, 'day') + 1;
        const shift = startOfMonth.day();

        for (let i = 0; i < shift; i += 1) {
            const prev = startOfMonth.subtract(shift - i, 'day');
            days.push({ key: `prev-${i}`, label: prev.date(), date: prev, disabled: true });
        }

        for (let day = 1; day <= totalDays; day += 1) {
            const current = dayjs(monthDate).date(day);
            const key = current.format('YYYY-MM-DD');
            const isSelected = key === tempStartDate || key === tempEndDate;
            const isInRange = tempStartDate && tempEndDate && dayjs(current).isAfter(dayjs(tempStartDate)) && dayjs(current).isBefore(dayjs(tempEndDate));
            const past = current.isBefore(dayjs(), 'day');
            days.push({ key, label: day, date: current, isSelected, isInRange, past });
        }

        return days;
    };

    const renderGrid = () => (daysInMonth(currentMonth).map(({ key, label, date, isSelected, isInRange, past, disabled }) => (
        <Box
            key={key}
            onClick={() => (!past && !disabled ? handleDateClick(date) : null)}
            sx={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                m: 0.25,
                borderRadius: 1,
                fontSize: '0.75rem',
                cursor: past || disabled ? 'not-allowed' : 'pointer',
                bgcolor: isSelected ? 'primary.main' : isInRange ? 'primary.100' : 'transparent',
                color: isSelected ? '#fff' : past ? 'text.disabled' : 'text.primary'
            }}
        >
            {label}
        </Box>
    )));

    return (
        <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
            <TextField
                label={label}
                value={getDisplayText()}
                onClick={() => setIsOpen((prev) => !prev)}
                fullWidth
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <IconButton onClick={() => setIsOpen((prev) => !prev)}>
                            <CalendarTodayIcon fontSize="small" />
                        </IconButton>
                    )
                }}
            />
            {isOpen && (
                <Paper
                    sx={{
                        position: 'absolute',
                        zIndex: 1200,
                        width: 320,
                        mt: 1,
                        p: 1
                    }}
                    elevation={8}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {selectingStart ? '🛫 Select departure date' : '🛬 Select return date'}
                        </Typography>
                        <Button size="small" onClick={handleStartNewSelection}>
                            Reset
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => setCurrentMonth((prev) => prev.subtract(1, 'month'))}
                            disabled={currentMonth.isSame(dayjs(), 'month')}
                        >
                            ←
                        </IconButton>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {currentMonth.format('MMMM YYYY')}
                        </Typography>
                        <IconButton size="small" onClick={() => setCurrentMonth((prev) => prev.add(1, 'month'))}>
                            →
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                        {DAY_HEADERS.map((day) => (
                            <Box
                                key={day}
                                sx={{ p: 1, textAlign: 'center', fontSize: '12px', fontWeight: 'bold', color: 'text.secondary' }}
                            >
                                {day}
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {renderGrid()}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default ExpediaDateRangeInput;
